import React, { type ErrorInfo } from 'react';
import { DefaultFallback, FallbackProps } from './DefaultFallback';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<FallbackProps>;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    name?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

/**
 * Implements a custom error boundary component that will catch errors in the app and display a fallback UI
 */
export class ErrorBoundaryComponent extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log the error
        console.error(
            `ErrorBoundary caught an error${this.props.name ? ` in ${this.props.name}` : ''}:`,
            {
                name: this.props.name || 'ApplicationError',
                error,
                errorInfo,
            },
        );

        // Call the onError callback if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    resetError = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError) {
            const FallbackComponent = this.props.fallback || DefaultFallback;
            return (
                <FallbackComponent
                    error={this.state.error}
                    resetError={this.resetError}
                    name={this.props.name}
                />
            );
        }

        return this.props.children;
    }
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, fallback, onError }) => {
    const handleError = (error: Error, errorInfo: ErrorInfo) => {
        if (onError) {
            onError(error, errorInfo);
        }
    };
    return (
        <ErrorBoundaryComponent onError={handleError} fallback={fallback}>
            {children}
        </ErrorBoundaryComponent>
    );
};
